import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { createContainer } from 'unstated-next';

import { CREATE_USER } from '../../config/apiPaths';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { type ClassResponse, getClassMeeting } from '../../utils/getClassMeeting';
import { parseOauthState } from '../../utils/oauthState';
import { getUrls } from '../../utils/url';

export enum SignUpErrorTypes {
    ERROR_AUTH_INVALID_CREDENTIALS = 1001,
    ERROR_AUTH_BAD_REQUEST = 1002,
    ERROR_AUTH_SERVER_ERROR = 1003,
    ERROR_AUTH_NO_RESPONSE = 1004,
    ERROR_AUTH_EMAIL_IN_USE = 1006,
    ERROR_AUTH_UNKNOWN = 1005
}

const useSignUpContainer = () => {
    const { api } = getUrls();
    const history = useHistory();

    const { loading: schoolLoading, error: schoolError, data: school } = useSchoolInfo();
    const [isOriginMeeting, setIsOriginMeeting] = useState(false);

    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState<SignUpErrorTypes | null>(null);
    const [meetingData, setMeetingData] =
        useState<{
            className: ClassResponse['class_name'];
            classTimes: ClassResponse['data']['class_times'];
        } | null>(null);

    useEffect(() => {
        const fetchMeetingData = async () => {
            const state = parseOauthState();
            if (!state || !state.redirect) return;
            const url = new URL(state.redirect);
            if (!url.pathname.includes('meeting/')) {
                return;
            }

            setIsOriginMeeting(true);

            const classUuid = url.searchParams.get('class_uuid');

            if (!classUuid) return;

            try {
                const response = await getClassMeeting(classUuid);
                setMeetingData({
                    className: response.class_name,
                    classTimes: response.data.class_times
                });
            } catch (e) {
                console.error(e);
            }
        };

        fetchMeetingData();
    }, []);

    const redirect = useCallback(
        (state) => {
            delete state.temp_session_id;

            const params = new URLSearchParams({
                state: JSON.stringify(state)
            });

            history.push({
                pathname: 'auth',
                search: params.toString()
            });
        },
        [history]
    );

    const backToStateUrl = useCallback(() => {
        const state = parseOauthState();
        redirect(state);
    }, [redirect]);

    /**
     * Initiate the Sign up process and redirect the user
     * according to the redirect url param.
     */
    const signUp = useCallback(
        ({ email, password, name }) => {
            const authenticate = async () => {
                setIsAuthenticating(true);
                setAuthError(null);

                try {
                    const createAccountResponse = await axios.post(`${api}${CREATE_USER}`, {
                        email,
                        password,
                        user_name: name
                    });

                    if (createAccountResponse.data.token) {
                        console.log('User created successfully.');
                        localStorage.setItem('user_uuid', createAccountResponse.data.token);
                        backToStateUrl();
                    } else {
                        setAuthError(SignUpErrorTypes.ERROR_AUTH_SERVER_ERROR);
                    }
                } catch (err: any) {
                    console.log('Error creating user: ', err);
                    if (err.response.data.error) {
                        const errorMessage = err.response.data.error as string;
                        switch (err.response.status) {
                            case 400:
                                setAuthError(SignUpErrorTypes.ERROR_AUTH_BAD_REQUEST);
                                break;
                            case 401:
                                setAuthError(SignUpErrorTypes.ERROR_AUTH_INVALID_CREDENTIALS);
                                break;
                            case 409:
                                if (errorMessage.includes('Could not add email address')) {
                                    setAuthError(SignUpErrorTypes.ERROR_AUTH_EMAIL_IN_USE);
                                } else {
                                    setAuthError(SignUpErrorTypes.ERROR_AUTH_SERVER_ERROR);
                                }
                                break;
                            default:
                                setAuthError(SignUpErrorTypes.ERROR_AUTH_SERVER_ERROR);
                        }

                        console.error(`Request failed with status: ${err.response.status}.`);
                        console.error(err.response.data);
                    } else if (err.request) {
                        setAuthError(SignUpErrorTypes.ERROR_AUTH_NO_RESPONSE);
                        console.error(err.request);
                    } else {
                        setAuthError(SignUpErrorTypes.ERROR_AUTH_UNKNOWN);
                        console.error(err);
                    }

                    setIsAuthenticating(false);
                }
            };

            authenticate();
        },
        [api, backToStateUrl]
    );

    return {
        isOriginMeeting,
        meetingData,
        isLoading: schoolLoading,
        error: schoolError,
        school,
        signUp,
        backToStateUrl,
        isAuthenticating,
        authError
    };
};

const SignUpContainer = createContainer(useSignUpContainer);

export default SignUpContainer;
